/**
 * Procedurele indeling. Afmetingen en aantallen komen uit de sceneconfig, zodat
 * het instellingenmenu het magazijn live opnieuw kan opbouwen.
 */
import { useMemo } from 'react'
import { useMagazijnConfig } from '../scene/store'
import type { MagazijnConfig } from '../scene/config'
import type { Scherm } from '../state/store'
import { OMLOOP } from './omloop'

export const HAL = { breedte: 58, diepte: 40, hoogte: 9.4 }

export const STELLING = {
  bayBreedte: 2.7,
  diepte: 1.1,
  staanderDikte: 0.11,
  liggerHoogte: 0.14,
  niveauHoogte: 1.9,
}

export const GANGPAD = { breedte: 3.4 }

/** Stellingen staan rug aan rug: gangpad + twee stellingdieptes. */
const GANGPAD_AFSTAND = GANGPAD.breedte + 2 * STELLING.diepte
/** Van het midden van een gangpad tot het midden van de stelling ernaast. */
export const STELLING_OFFSET = GANGPAD.breedte / 2 + STELLING.diepte / 2

export interface Indeling {
  cfg: MagazijnConfig
  gangpadZ: (i: number) => number
  bayX: (i: number) => number
  stellingHoogte: number
  wanden: { z: number; kant: -1 | 1 }[]
  /** Buitenste z-grens van de stellingblokken. */
  blokDiepte: number
  /**
   * Het vak dat in elke stelling openblijft als dwarsgang: een doorgang
   * loodrecht op de gangpaden, waar de C-MATIC de stellingen kruist. `null`
   * bij een hal met te weinig vakken om er een te missen.
   */
  dwarsgang: number | null
}

export function maakIndeling(cfg: MagazijnConfig): Indeling {
  const gangpadZ = (i: number) => (i - (cfg.gangpaden - 1) / 2) * GANGPAD_AFSTAND
  const bayX = (i: number) => (i - (cfg.bays - 1) / 2) * STELLING.bayBreedte

  const wanden: { z: number; kant: -1 | 1 }[] = []
  for (let i = 0; i < cfg.gangpaden; i++) {
    wanden.push({ z: gangpadZ(i) - STELLING_OFFSET, kant: -1 })
    wanden.push({ z: gangpadZ(i) + STELLING_OFFSET, kant: 1 })
  }

  return {
    cfg,
    gangpadZ,
    bayX,
    stellingHoogte: cfg.niveaus * STELLING.niveauHoogte,
    wanden,
    blokDiepte: Math.abs(gangpadZ(0) - STELLING_OFFSET) + STELLING.diepte,
    // Vier vakken voor de kop aan de kant van het hoofdscherm: daar kijkt de
    // camera door gangpad 04 heen, en daar reed eerder de heftruck.
    dwarsgang: cfg.bays >= 8 ? Math.round(cfg.bays * 0.8) : null,
  }
}

export function useIndeling(): Indeling {
  const cfg = useMagazijnConfig()
  return useMemo(() => maakIndeling(cfg), [cfg])
}

/** Mulberry32 — kleine, snelle, deterministische PRNG. */
export function prng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export interface Camerastand {
  positie: [number, number, number]
  doel: [number, number, number]
}

/**
 * Camerastandpunten per scherm. Absoluut, dus onafhankelijk van de indeling.
 * Getypeerd op `Scherm`: een onderdeel zonder stand viel eerder stil terug op
 * het hoofdscherm.
 */
export const STANDEN: Record<Scherm, Camerastand> = {
  hoofdscherm:    { positie: [33, 3.6, 4.3], doel: [-20, 4.0, 14.5] },
  automatisering: { positie: [-38, 6.4, 19], doel: [4, 2.8, -3] },
  veiligheid:     { positie: [6, 2.2, 21], doel: [-8, 1.8, 1] },
  energie:        { positie: [-33, 5.0, 18], doel: [-14, 2.2, 4] },
  connectiviteit: { positie: [0, 23, 38], doel: [0, 4, 0] },
  // Stellingen (21 oktober): in de kop van gangpad 05, waar geen truck rijdt,
  // vlak langs de stellingwand rechts in beeld — staanders, liggers, schoren
  // en aanrijdbeveiliging zijn hier het onderwerp, links blijft het gangpad
  // diep en rustig achter de tekst.
  stellingen:     { positie: [27, 2.0, 12.8], doel: [15, 2.8, 9.4] },
  // Tennant: schuin over de open vloer vóór de stellingen, waar de
  // schrobmachine zijn ellips rijdt (x 7–21, z 20,5–28,5); hij komt zo in de
  // rechterhelft langs, met de koppen van de stellingen en de leuning erachter.
  // Achter de stellingen, bij de docks, staat geen open vloer in beeld.
  tennant:        { positie: [31, 2.8, 32], doel: [12, 0.0, 23.5] },
}

/**
 * De zweefroute van het hoofdscherm (feedback 5 oktober): de camera staat daar
 * niet meer stil met een beetje drift, maar zweeft in een gesloten lus door de
 * hal langs de plekken van alle vijf onderdelen. Wie een tegel aantikt, vliegt
 * vanaf het punt waar de lus op dat moment is naar het standpunt van dat
 * onderdeel — één beweging, geen knip.
 *
 * De punten liggen bewust buiten de stellingblokken (x binnen ±25,7 en z binnen
 * ±14 bij de standaardindeling, 7,6 m hoog), ín een gangpad op mensenhoogte, of
 * in de dwarsgang (x 14,85–17,55: het vak dat in elke stelling openblijft).
 * Rechtstreeks van standpunt naar standpunt gaat dwars door de stellingen; de
 * tussenpunten hieronder leiden eromheen. De route wordt als Catmull-Rom-kromme
 * door deze punten getrokken, dus elk punt is een doorgang, geen halte.
 *
 * **Langs de Matics** (22 oktober, met de film van de zweefronde). De route
 * hing eerst ruim een derde van de tijd boven het dak, en de Matics waren er
 * stippen in de verte. Nu gaat hij door gangpad 04 langs de L-MATIC, en dan
 * door de dwarsgang, waar de C-MATIC rijdt, langs de koppen van gangpad 03, 02
 * en 01: bij elk gangpad kijkt de camera het pad in, naar de R-, de T- en de
 * K-MATIC, op 4 tot 19 m. Daar zweeft hij op halve snelheid (`tempo`), elke
 * truck zo'n tien seconden in beeld. Daarna achterlangs, de westkant (Energie,
 * Automatisering), de voorzijde (Veiligheid), kort omhoog onder het dak
 * (Connectiviteit), langs de schrobmachine (Tennant) en terug.
 *
 * `tempo` is de snelheid ten opzichte van de rest van de route, glad
 * overgaand van punt naar punt (`CameraRig.tsx`); standaard 1.
 */
export interface Zweefpunt extends Camerastand {
  tempo?: number
}

export const ZWEEFROUTE: Zweefpunt[] = [
  { positie: [33, 3.6, 4.3],     doel: [-20, 4.0, 14.5] },              // hoofdscherm
  { positie: [25, 2.7, 5.6],     doel: [4, 1.6, 5.6], tempo: 0.8 },     // gangpad 04 in
  { positie: [19.5, 2.5, 5.5],   doel: [7, 1.2, 5.6], tempo: 0.5 },     // de L-MATIC
  { positie: [16.6, 2.6, 4.4],   doel: [5, 1.3, 2.0], tempo: 0.5 },     // de dwarsgang in
  { positie: [16.2, 2.7, 0.6],   doel: [3, 1.5, 0], tempo: 0.5 },       // gangpad 03: de R-MATIC
  { positie: [16.2, 2.7, -4.6],  doel: [5, 1.0, -5.6], tempo: 0.5 },    // gangpad 02: de T-MATIC
  { positie: [16.2, 3.0, -10.0], doel: [5, 2.4, -10.8], tempo: 0.5 },   // gangpad 01: de K-MATIC
  { positie: [16.4, 3.4, -16.0], doel: [12, 1.2, -9], tempo: 0.7 },     // de dwarsgang uit, langs station A van de C-MATIC
  { positie: [4, 4.5, -22],      doel: [-10, 2.5, -12] },               // achterlangs
  { positie: [-30, 5, -20],      doel: [-12, 2.4, -2] },                // hoek achter-west
  { positie: [-34, 5, 2],        doel: [-14, 2.2, 6] },                 // westkant: automatisering
  { positie: [-33, 5.0, 18],     doel: [-14, 2.2, 4] },                 // energie
  { positie: [-16, 4.0, 22],     doel: [0, 2.4, 2] },                   // langs de voorzijde
  { positie: [6, 2.4, 21],       doel: [-8, 1.8, 1] },                  // veiligheid
  { positie: [10, 7.2, 31],      doel: [0, 2.5, 0] },                   // kort omhoog, onder het dak: connectiviteit
  { positie: [31, 3.2, 28],      doel: [13, 0.4, 23.5], tempo: 0.8 },   // tennant: de schrobmachine
  { positie: [37, 4.0, 14],      doel: [0, 3, 8] },                     // oostkant, terug naar het begin
]

/**
 * Omlooptijd van de zweefroute in seconden. Traag: het is zweven, geen rit.
 * Gelijk aan de omloop van de hal (`omloop.ts`), want de film van de
 * zweefronde moet naadloos rondlopen: 245 s sinds 22 oktober, was 210.
 */
export const ZWEEF_OMLOOP = OMLOOP

/**
 * De werkgang van de truck die bij een hotspot hoort, op de x-as en relatief aan
 * de hotspot. Camera en route komen uit dezelfde plek, want ze horen bij elkaar:
 * verschuift de één, dan rijdt de truck uit beeld.
 *
 * De route ligt bewust vóór de camera (van −4 tot +6 m om de hotspot, camera op
 * −11,5): dichtstbij 7,5 m, verst 17,5 m. Dichterbij en hij rijdt door de lens,
 * verder en hij wordt een stip.
 */
export const WERKGANG = { anker: 1, bereik: 5 }

/**
 * Camerastand vóór een hotspot, iets uit het hart van het gangpad zodat je de
 * stellingwand schuin ziet. De z-offset blijft binnen het gangpad. De hoogte is
 * die van een mens in het gangpad: op 3,3 m keek je over de machine heen.
 */
export const hotspotStand = (ind: Indeling, x: number, gangpadIndex: number): Camerastand => ({
  positie: [x - 11.5, 2.3, ind.gangpadZ(gangpadIndex) - 1.35],
  // De kijkas loopt links langs de truck en iets onder hem door. Dat zet hem in de
  // rechterhelft van het beeld, want links staat de tekst en onderin de knoppenrij:
  // het vrije vlak van de indeling is rechtsboven.
  doel: [x + 1.5, 1.05, ind.gangpadZ(gangpadIndex) - 2.4],
})
